import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { openDesktopTab } from "@/desktop/tabs";
import { useDeckSession } from "@/hooks/useDeckSession";
import { DeckEditor } from "@/pages/DeckEditor";
import { hydrateStore } from "@/store/bootstrap";
import { DeckSkeleton, HydrationGate } from "@/store/HydrationGate";

// `/d/:deckId` — the editor for one deck (TODO §12.1.1).
//
// The open-deck pointer lives in the URL, not in storage, and that is the whole point
// (§12.1.3): a stored "current deck" setting is one value that two pages fight over, whereas
// a URL is per-page by construction. It also survives reload, it is linkable, and it is what
// a second Electron window will load (§14.7.2).
//
// This component owns the two things that must outlive an editor remount: the deck's write
// session (§12.5) and the hydration gate. `DeckEditor` itself is keyed on `deckId` so a
// switch discards every scrap of per-deck UI state by remounting (§12.2.4).
export function DeckRoute() {
  const { deckId } = useParams<{ deckId: string }>();
  const session = useDeckSession(deckId ?? "");
  const [takingOver, setTakingOver] = useState(false);

  // §14.7.1 Every way into a deck — the strip, the documents home, a menu command, a restored
  // window, a link — arrives here, so this is the one place a tab has to be registered. A
  // no-op outside the desktop shell.
  useEffect(() => {
    if (deckId) openDesktopTab(deckId);
  }, [deckId]);

  // §12.5.2 "Edit here". Re-hydrating afterwards is not optional: this page has been a
  // passive viewer, and whatever it holds is at best as fresh as the last broadcast.
  const handleTakeOver = useCallback(async () => {
    if (!deckId) return;
    setTakingOver(true);
    try {
      const owned = await session.takeOver();
      if (!owned) {
        toast.error("The other window is still editing this deck");
        return;
      }
      await hydrateStore(deckId);
      toast.success("You’re editing this deck now");
    } finally {
      setTakingOver(false);
    }
  }, [deckId, session]);

  // A `/d/` with no id is not a deck — send it somewhere useful rather than 404ing.
  if (!deckId) return <Navigate to="/documents" replace />;
  // Hold the skeleton until we know whether we own the deck, so the editor never comes up
  // editable and then demotes itself under the user's hands.
  if (!session.ready) return <DeckSkeleton />;

  return (
    <HydrationGate
      key={deckId}
      deckId={deckId}
      followExternalUpdates={!session.writable}
    >
      <DeckEditor
        key={deckId}
        readOnly={!session.writable}
        takingOver={takingOver}
        onTakeOver={handleTakeOver}
      />
    </HydrationGate>
  );
}
